import { Canvas, type FabricImage, type ImageFormat } from "fabric";
import { getInstance } from "../../../../../../ffmpeg/getInstance.ts";
import type { RgbaColor } from "../../../../../../types.ts";
import { rgbaToString } from "../../../../../../utils/colors.ts";
import type { CanvasSize } from "../types.ts";
import { getSingleAssetLayer } from "./layer.ts";

export const buildCanvasWithImageContent = async (
  fabricCanvas: Canvas,
  canvasSize: CanvasSize,
  contentUrl: string,
  imageFormat: ImageFormat,
  quality: number,
): Promise<Blob> => {
  const combinedCanvas = document.createElement("canvas");
  const ctx = combinedCanvas.getContext("2d");
  if (!ctx) throw new Error("Failed to get canvas context");

  combinedCanvas.width = canvasSize.width;
  combinedCanvas.height = canvasSize.height;

  const canvasData = fabricCanvas.toJSON();
  const canvasWithContent = new Canvas(document.createElement("canvas"), {
    preserveObjectStacking: true,
  });

  await canvasWithContent.loadFromJSON(canvasData, () => {
    canvasWithContent.width = fabricCanvas.getWidth();
    canvasWithContent.height = fabricCanvas.getHeight();
  });
  const assetLayer = getSingleAssetLayer(canvasWithContent);
  if (assetLayer) {
    const originalWidth = assetLayer.width;
    const originalHeight = assetLayer.height;
    const originalScaleX = assetLayer.scaleX;
    const originalScaleY = assetLayer.scaleY;

    await (assetLayer as FabricImage).setSrc(contentUrl, {
      crossOrigin: "anonymous",
    });

    assetLayer.set({
      width: originalWidth,
      height: originalHeight,
      scaleX: originalScaleX,
      scaleY: originalScaleY,
    });
  }
  canvasWithContent.renderAll();

  let canvasWithContentUrl: string;
  try {
    canvasWithContentUrl = canvasWithContent.toDataURL({
      format: imageFormat,
      quality,
      multiplier: canvasSize.width / fabricCanvas.getWidth(),
    });
  } catch (error) {
    console.warn("Canvas is tainted, falling back to toCanvasElement:", error);
    const canvasElement = fabricCanvas.toCanvasElement(
      canvasSize.width / fabricCanvas.getWidth(),
    );
    canvasWithContentUrl = canvasElement.toDataURL(
      `image/${imageFormat}`,
      quality,
    );
  }

  const canvasImage = new Image();
  canvasImage.crossOrigin = "anonymous";
  await new Promise((resolve, reject) => {
    canvasImage.onload = resolve;
    canvasImage.onerror = reject;
    canvasImage.src = canvasWithContentUrl;
  });

  ctx.drawImage(canvasImage, 0, 0, canvasSize.width, canvasSize.height);

  return await new Promise((resolve, reject) => {
    combinedCanvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to create blob"));
      },
      `image/${imageFormat}`,
      quality,
    );
  });
};

export const buildCanvasWithVideoContent = async (
  fabricCanvas: Canvas,
  canvasSize: CanvasSize,
  canvasBackgroundColor: RgbaColor,
  contentUrl: string,
): Promise<Blob> => {
  const ffmpeg = await getInstance();

  // XXX: FFmpeg requires even dimensions for video encoding (H.264 codec requirement)
  // Odd dimensions cause "Error while opening encoder" issues
  // We round up to the nearest even number to ensure compatibility
  // Use original video dimensions for output to preserve video size
  const outputWidth = Math.ceil(canvasSize.width / 2) * 2;
  const outputHeight = Math.ceil(canvasSize.height / 2) * 2;
  const canvasWidth = Math.ceil(fabricCanvas.getWidth() / 2) * 2;
  const canvasHeight = Math.ceil(fabricCanvas.getHeight() / 2) * 2;

  const canvasData = fabricCanvas.toJSON();
  delete canvasData.backgroundImage;
  const noBackgroundCanvas = new Canvas(document.createElement("canvas"), {
    preserveObjectStacking: true,
  });

  const assetLayer = getSingleAssetLayer(fabricCanvas);

  const scaleX = outputWidth / canvasWidth;
  const scaleY = outputHeight / canvasHeight;

  const videoPosition = {
    x: Math.round(
      assetLayer
        ? (assetLayer.left - (assetLayer.width * assetLayer.scaleX) / 2) *
            scaleX
        : 0,
    ),
    y: Math.round(
      assetLayer
        ? (assetLayer.top - (assetLayer.height * assetLayer.scaleY) / 2) *
            scaleY
        : 0,
    ),
    width: Math.round(
      assetLayer ? assetLayer.width * assetLayer.scaleX * scaleX : outputWidth,
    ),
    height: Math.round(
      assetLayer
        ? assetLayer.height * assetLayer.scaleY * scaleY
        : outputHeight,
    ),
  };

  await noBackgroundCanvas.loadFromJSON(canvasData);

  noBackgroundCanvas.width = canvasWidth;
  noBackgroundCanvas.height = canvasHeight;
  noBackgroundCanvas.backgroundColor = "";

  if (assetLayer) noBackgroundCanvas.remove(assetLayer);
  noBackgroundCanvas.renderAll();

  let noBackgroundCanvasUrl: string;
  try {
    noBackgroundCanvasUrl = noBackgroundCanvas.toDataURL({
      format: "png",
      quality: 1.0,
      multiplier: outputWidth / noBackgroundCanvas.getWidth(),
    });
  } catch (error) {
    console.warn("Canvas is tainted, falling back to toCanvasElement:", error);
    const canvasElement = noBackgroundCanvas.toCanvasElement(
      outputWidth / noBackgroundCanvas.getWidth(),
    );
    noBackgroundCanvasUrl = canvasElement.toDataURL("image/png", 1.0);
  }

  await noBackgroundCanvas.dispose();

  const noBackgroundCanvasBlob = await (
    await fetch(noBackgroundCanvasUrl)
  ).blob();
  const noBackgroundCanvasBuffer = new Uint8Array(
    await noBackgroundCanvasBlob.arrayBuffer(),
  );

  const backgroundCanvas = new Canvas(document.createElement("canvas"), {
    preserveObjectStacking: true,
  });
  backgroundCanvas.width = outputWidth;
  backgroundCanvas.height = outputHeight;

  backgroundCanvas.backgroundColor = rgbaToString(canvasBackgroundColor);
  backgroundCanvas.renderAll();

  const backgroundCanvasBlob = await (
    await fetch(
      backgroundCanvas.toDataURL({
        format: "png",
        quality: 1.0,
        multiplier: 1,
      }),
    )
  ).blob();
  const backgroundCanvasBuffer = new Uint8Array(
    await backgroundCanvasBlob.arrayBuffer(),
  );

  const videoRes = await fetch(contentUrl);
  if (!videoRes.ok) throw new Error("Failed to fetch video");
  const videoBuffer = new Uint8Array(await videoRes.arrayBuffer());

  const filesToCleanup: string[] = [];

  try {
    await ffmpeg.writeFile("background.png", backgroundCanvasBuffer);
    filesToCleanup.push("background.png");

    await ffmpeg.writeFile("input.mp4", videoBuffer);
    filesToCleanup.push("input.mp4");

    await ffmpeg.writeFile("overlay.png", noBackgroundCanvasBuffer);
    filesToCleanup.push("overlay.png");

    const outputFilename = "output.mp4";

    await ffmpeg.exec([
      "-i",
      "background.png",
      "-i",
      "input.mp4",
      "-i",
      "overlay.png",
      "-filter_complex",
      `[1:v] scale=${videoPosition.width}:${videoPosition.height} [scaled_video]; [0:v][scaled_video] overlay=${videoPosition.x}:${videoPosition.y} [tmp]; [tmp][2:v] overlay=0:0`,
      "-preset",
      "ultrafast",
      "-c:a",
      "copy",
      outputFilename,
    ]);
    filesToCleanup.push(outputFilename);

    const out = await ffmpeg.readFile(outputFilename);
    return new Blob([out], { type: "video/mp4" });
  } finally {
    for (const filename of filesToCleanup) {
      try {
        await ffmpeg.deleteFile(filename);
      } catch (error) {
        console.warn(`Failed to delete ${filename}:`, error);
      }
    }
  }
};
